import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import OrderSearchSection from "../../../../../../components/search-sections/order";
import {routeFunctions, routes} from "../../../../../../../core/models/static/routes/app-routes";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import Api from "../../../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {BackOrdersListsItemsDataGridCellKeys, BackOrdersListsOrdersDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import BackOrdersListViewItemsTable from "./sections/items";
import BackOrdersListViewOrdersTable from "./sections/orders";
import {useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {Link, useHistory} from "react-router-dom";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import Button from "../../../../../../components/base/button/button";
import {useAppSelector} from "../../../../../../redux";
import useQuery from "../../../../../../hooks/use-query";
import CreateUserOnlineProfileDialog from "../../../../../../components/dialogs/create-user-online-profile";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import {stringifyUrl} from "query-string";


const tabs = {
    orders: 'orders',
    items: 'items',
};

const tabsList = [
    {
        title: "Orders",
        value: tabs.orders,
    },
    {
        title: "Items",
        value: tabs.items,
    }
]
const defaultTab = tabs.orders;

const ordersOrderByPropNames = Object
    .values(BackOrdersListsOrdersDataGridCellKeys)
    .filter(e => [
        BackOrdersListsOrdersDataGridCellKeys.navigate,
        BackOrdersListsOrdersDataGridCellKeys.revertToQuote,
        BackOrdersListsOrdersDataGridCellKeys.invoiceNo,
    ].includes(e));
const itemsOrderByPropNames = Object
    .values(BackOrdersListsItemsDataGridCellKeys)
    .filter(e => [
        BackOrdersListsItemsDataGridCellKeys.navigate,
    ].includes(e));

const exportingChunkSize = 100;

const initNewOnlineProfileDialogState = {open: false, initialValues: null, onSuccess: () => void 0, onClose: () => void 0};
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};

const BackOrdersListView = () => {
    const history = useHistory();
    const [query] = useQuery();
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const [updatedTab, setUpdatedTab] = useState(query?.tab ?? defaultTab);
    const [fetchingOrdersCustomer, setFetchingOrdersCustomer] = useState([]);
    const [onlineUserProfileDialog, setOnlineUserProfileDialog] = useState(initNewOnlineProfileDialogState);
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('back-orders-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (defaultCustomer?.code) {
            configureNotebookTags((tags) => tags.customerSalesBackOrders(defaultCustomer?.code));
            configureBehavioralTool((tags => tags.customerSalesBackOrders(defaultCustomer?.code)));
        } else {
            configureNotebookTags((tags) => tags.salesBackOrders);
            configureBehavioralTool((tags) => tags.salesBackOrders);
        }
    }, [configureNotebookTags, configureBehavioralTool, defaultCustomer?.code]);

    /**
     * Prepares the search request for search api with the given parameters.
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareSearchData = useCallback((args) => {
        const res = {
            filters: {
                customerId: defaultCustomer?.id ? [defaultCustomer?.id] : args.customers?.map(e => e.id),
                fromDate: args.filters?.startDate,
                ToDate: args.filters?.endDate ?? DateUtils.serverNow().add('1', 'day').endOf('day').format(DateUtils.withoutOffsetFormat),
                Keyword: args.filters?.keyword?.toString(),
            },
            paginationInfo: args.paginationInfo,
        };
        // only when the items tab is visible, we use partNumber field
        if (args.tab === tabs.items) {
            res.filters['partNo'] = args.filters?.partNo;
        }
        if (args.orderBy) {
            switch (args.tab) {
                case tabs.items:
                    if (itemsOrderByPropNames.includes(args.orderBy.field)) {
                        res['orderByProperties'] = [{
                            propertyName: args.orderBy.field,
                            isDescending: args.orderBy.descending
                        }]
                    }
                    break;
                case tabs.orders:
                default:
                    if (ordersOrderByPropNames.includes(args.orderBy.field)) {
                        res['orderByProperties'] = [{
                            propertyName: args.orderBy.field,
                            isDescending: args.orderBy.descending
                        }]
                    }
                    break;
            }
        }
        return res;
    }, [defaultCustomer?.id])

    /**
     * Fetches the orders based on the given parameters.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareSearchData(args);
        let response;
        switch (args.tab) {
            case tabs.items:
                response = await Api.searchBackOrderItems(forApi, args.cancelTokenSource?.token);
                break;
            case tabs.orders:
            default:
                response = await Api.searchBackOrders(forApi, args.cancelTokenSource?.token);
                break;
        }
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.resultList ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.paginationInfo?.length ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareSearchData])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const [ordersDataGridSaveConfig, itemsDataGridSaveConfig] = useMemo(() => [
        ({
            name: 'BackOrdersListViewOrdersDataGrid',
            version: 2,
        }),
        ({
            name: 'BackOrdersListViewItemsDataGrid',
            version: 2,
        })
    ], [])

    const [, ordersDataGridSortBy, ordersDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: ordersDataGridSaveConfig.name,
        version: ordersDataGridSaveConfig.version,
    });
    const [, itemsDataGridSortBy, itemsDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: itemsDataGridSaveConfig.name,
        version: itemsDataGridSaveConfig.version,
    });
    const [dataGridSortBy, dataGridPageSize] = useMemo(() => {
        switch (updatedTab) {
            case tabs.orders:
                return [ordersDataGridSortBy, ordersDataGridPageSize]
            case tabs.items:
                return [itemsDataGridSortBy, itemsDataGridPageSize]
            default:
                return [];
        }
    }, [itemsDataGridPageSize, itemsDataGridSortBy, ordersDataGridPageSize, ordersDataGridSortBy, updatedTab])

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        customers: [],
    }), [updatedTab, initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @param {any[]} list
     * @return {DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        switch (searchData.tab) {
            case tabs.items:
                return {
                    title: `Back order Items`,
                    body: list?.map(e => ({
                        'Order No': e.orderNo ?? '',
                        'Customer': e.customer?.name ?? '',
                        'Part Number': e.partNo ?? '',
                        'Order Date': e.createdDate ? DateUtils.serverToLocal(e.createdDate).format(DateUtils.dateFormat) : '',
                        'Unit Price': formatMoney(e.unitPrice ?? ''),
                        'Order Quantity': e.orderQty ?? '',
                    })) ?? [],
                }
            case tabs.orders:
            default:
                return {
                    title: `Back orders`,
                    body: list?.map(e => ({
                        'Order No': e.orderNo ?? '',
                        'Customer': e.customer?.name ?? '',
                        'Order Date': e.orderDate ? DateUtils.serverToLocal(e.orderDate).format(DateUtils.dateFormat) : '',
                        'PO-Number': e.customerPO ?? '',
                        'Sales Person No': e.salespersonNo ?? '',
                        'Total Ordered': formatMoney(e.total ?? ''),
                        'Status': e.status ?? '',
                    })) ?? [],
                }
        }
    }, [searchData.tab])


    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportBackOrdersList = operatorAccessLevels.packages.sales.exportBackOrdersList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    /**
     * Handles changes in the tabs of the orders view.
     */
    const onTabChanged = useCallback((e, newTab) => {
        setData([]);
        searchData.setTab(newTab);
        switch (newTab) {
            case tabs.orders:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: ordersDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(ordersDataGridSortBy);
                break;
            case tabs.items:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: itemsDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(itemsDataGridSortBy);
                break;
            default:
                return;
        }
    }, [initialPaginationInfo.pageSize, itemsDataGridPageSize, itemsDataGridSortBy, ordersDataGridPageSize, ordersDataGridSortBy, searchData])

    /**
     * Navigates the operator to the single order view of the given order.
     * @type {(function(*, *): void)}
     */
    const setLoading = useCallback((orderId, loading) => {
        switch (searchData.tab) {
            case tabs.items:
                setData(prevState => prevState?.map(e => e.orderId === orderId ? {...e, loading: loading} : e));
                break;
            case tabs.orders:
            default:
                setData(prevState => prevState?.map(e => e.id === orderId ? {...e, loading: loading} : e));
                break;
        }
    }, [searchData.tab,])

    /**
     * Fetches the customer id from the given code.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchCustomerIdFromCode = useCallback(async (customer, orderId) => {
        setFetchingOrdersCustomer(prevState => [...prevState, orderId]);
        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
        if (!isMounted())
            return;
        setFetchingOrdersCustomer(prevState => prevState.filter(e => e !== orderId));
        if (response.resultFlag) {
            return response.data?.id;
        } else {
            let succeeded = false;
            return await new Promise(resolve => {
                setOnlineUserProfileDialog({
                    open: true,
                    initialValues: {company: {id: customer.id, code: customer.customerNo, name: customer.name}},
                    onClose: () => {
                        if (!succeeded) {
                            resolve();
                        }
                        setOnlineUserProfileDialog(initNewOnlineProfileDialogState);
                    },
                    onSuccess: async () => {
                        succeeded = true;
                        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
                        resolve(response.data?.id);
                    }
                });
            })
        }
    }, [isMounted])

    /**
     * Navigates to the information view of the given customer and
     * @type {(function(*, *): Promise<void>)}
     */
    const navigateToInformationView = useCallback(async (customer, orderId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return;
        history.push(routeFunctions.customer.sales.backOrder.single(customerId, orderId));
    }, [fetchCustomerIdFromCode, isMounted, history])

    /**
     * Navigates to the print view of the given customer and order.
     * @type {(function(*, *): Promise<void>)}
     */
    const navigateToPrintView = useCallback(async (customer, orderId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return

        window.open(window.location.origin + stringifyUrl({
            url: routeFunctions.print.salesOrder(customerId, orderId),
            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
        }), '_blank');

        // setPrintOrderConfigurationsDialog({
        //     open: true,
        //     printUrl: routeFunctions.print.salesOrder(customerId, orderId),
        //     onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        // })
    }, [fetchCustomerIdFromCode, isMounted])

    /**
     * Navigates to the invoice view of the given customer and order
     * @type {(function(*, *): Promise<*>)}
     */
    const navigateToInvoiceView = useCallback(async (customer, orderId, invoiceId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return;
        window.open(window.location.origin + routeFunctions.customer.sales.invoices.single(customerId, invoiceId), '_blank');
    }, [fetchCustomerIdFromCode, isMounted])

    /**
     * Reverts the sales order associated with the given orderId to a quote.
     *
     * * if the response of the api is successful, navigates the user back to the single quote information of the
     * reverted sales order.
     * @type {(function(*, *): Promise<void>)}
     */
    const revertToQuote = useCallback(async (customer, orderId) => {
        const customerId = await fetchCustomerIdFromCode(customer, orderId);
        if (!customerId || !isMounted())
            return;
        setLoading(orderId, true);
        const response = await Api.revertToQuoteByCustomer(customerId, orderId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            return history.replace(routeFunctions.customer.sales.quotes.single(customerId, orderId));
        }
        setLoading(orderId, false);
    }, [fetchCustomerIdFromCode, isMounted, setLoading, history]);

    return (
        <>
            <div className='p-3 sales-views-top-progress-indicator-offset'>
                <div className={'d-flex justify-content-between align-content-center mb-4'}>
                    <p className={'view-title'}>
                        {defaultCustomer?.name ? `${defaultCustomer?.name}'s` : "System"} Back Orders
                    </p>
                    <div className={'d-flex align-content-center justify-content-end'}>
                        {
                            !!defaultCustomer?.id &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                component={Link}
                                to={routes.sales.backOrder}
                            >
                                View all back-orders
                            </Button>
                        }
                    </div>
                </div>
                <OrderSearchSection
                    searchData={searchData}
                    showPartNumberField={searchData.tab === tabs.items}
                    hideCustomersMultiSelect={!!defaultCustomer?.id}
                />
                <div className='my-2'/>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={searchData.tab} onChange={onTabChanged}>
                        {
                            tabsList.map(e => (
                                <Tab key={e.value} value={e.value} label={e.title}/>
                            ))
                        }
                    </Tabs>
                </div>
                <div className='my-4 w-100'/>
                {
                    searchData.tab === tabs.items &&
                    <BackOrdersListViewItemsTable
                        exportToCsv={canExportBackOrdersList ? exportToCsv : undefined}
                        searchData={searchData}
                        data={data}
                        dataGridSaveConfig={itemsDataGridSaveConfig}
                        fetchingOrdersCustomer={fetchingOrdersCustomer}
                        navigateToInformationView={navigateToInformationView}
                        navigateToPrintView={navigateToPrintView}
                        revertToQuote={revertToQuote}
                    />
                }
                {
                    searchData.tab === tabs.orders &&
                    <BackOrdersListViewOrdersTable
                        exportToCsv={canExportBackOrdersList ? exportToCsv : undefined}
                        searchData={searchData}
                        data={data}
                        dataGridSaveConfig={ordersDataGridSaveConfig}
                        fetchingOrdersCustomer={fetchingOrdersCustomer}
                        navigateToInformationView={navigateToInformationView}
                        navigateToInvoiceView={navigateToInvoiceView}
                        navigateToPrintView={navigateToPrintView}
                        revertToQuote={revertToQuote}
                    />
                }
            </div>
            <CreateUserOnlineProfileDialog
                open={onlineUserProfileDialog?.open}
                initialValues={onlineUserProfileDialog.initialValues}
                onSuccess={onlineUserProfileDialog.onSuccess}
                setOpen={onlineUserProfileDialog.onClose}
            />
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
        </>
    );
}

export default BackOrdersListView;
